import { Box, Stack, Typography } from '@mui/material';

import type { RoadmapLevel } from "../../api/roadmap-service";

import { beltColor, LANDMARKS } from "./belts";

const INK = '#1B2A4A';

export type RoadmapBeltPickerProps = {
    levels: RoadmapLevel[];
    value: number;
    onChange: (level: number) => void;
};

/** The target level as a row of karate belts; everything above the chosen one fades out on the map. */
export function RoadmapBeltPicker({ levels, value, onChange }: RoadmapBeltPickerProps) {
    const name = levels.find((level) => level.level === value)?.name ?? '';

    return (
        <Stack component="section" aria-label="Target level" spacing={ 1.75 }
               sx={ { bgcolor: '#FFFFFF', border: '1px solid #E3E8EE', borderRadius: '20px', px: 3, py: 2.5 } }>
            <Stack direction={ { xs: 'column', sm: 'row' } } justifyContent="space-between" alignItems={ { sm: 'baseline' } } spacing={ 0.5 }>
                <Typography component="h2" variant="inherit" sx={ { fontSize: 16, fontWeight: 800 } }>
                    Target belt: <Box component="span" sx={ { color: '#6A3FC0' } }>{ name }</Box>
                </Typography>
                <Typography sx={ { fontSize: 13, color: '#4F5B67' } }>Everything above your belt fades out on the map</Typography>
            </Stack>

            <Box role="group" aria-label="Level" sx={ {
                display: 'grid', gap: 1,
                gridTemplateColumns: { xs: 'repeat(4, minmax(0, 1fr))', sm: 'repeat(6, minmax(0, 1fr))', lg: 'repeat(12, minmax(0, 1fr))' },
            } }>
                { levels.map((level) => (
                    <Belt key={ level.level } level={ level } selected={ level.level === value } below={ level.level < value }
                          above={ level.level > value } onSelect={ () => onChange(level.level) }/>
                )) }
            </Box>
        </Stack>
    );
}

type BeltProps = {
    level: RoadmapLevel;
    selected: boolean;
    below: boolean;
    above: boolean;
    onSelect: () => void;
};

function Belt({ level, selected, below, above, onSelect }: BeltProps) {
    const color = beltColor(level.level);

    return (
        <Box component="button" type="button" aria-pressed={ selected } aria-label={ level.name } onClick={ onSelect } sx={ {
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, px: 0.5, pt: 1.25, pb: 1,
            borderRadius: '12px', border: `2px solid ${ selected ? '#8E5CD9' : 'transparent' }`,
            bgcolor: selected ? '#F5F0FF' : below ? '#F4F6F8' : '#FFFFFF',
            font: 'inherit', cursor: 'pointer', color: '#161C24', opacity: above ? 0.55 : 1,
            '&:hover': { opacity: 1 },
            '&:focus-visible': { outline: '2px solid #2065D1', outlineOffset: 2 },
        } }>
            <Box aria-hidden="true" sx={ { position: 'relative', width: '100%', height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' } }>
                <Box sx={ { width: '84%', height: 10, borderRadius: '3px', bgcolor: color, border: `1.5px solid ${ INK }`, boxSizing: 'border-box' } }/>
                <Box sx={ { position: 'absolute', width: 14, height: 14, borderRadius: '3px', bgcolor: color, border: `1.5px solid ${ INK }`, boxSizing: 'border-box' } }/>
            </Box>
            <Box component="span" sx={ { fontSize: 12, fontWeight: 800 } }>{ level.name }</Box>
            <Box component="span" sx={ { fontSize: 10, fontWeight: 700, color: '#4F5B67', minHeight: 12, lineHeight: '12px', textAlign: 'center' } }>
                { LANDMARKS[level.level] ?? '' }
            </Box>
        </Box>
    );
}
